import style from "./Footer.module.css";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Container from "@/layouts/container/Container";
import Icon from "@/ui/icon/Icon";
import Logo from "@/ui/logo/Logo";
import EuFunding from "@/ui/euFunding/EuFunding";
import LanguageSwitcher from "@/ui/languageSwitcher/LanguageSwitcher";
import { useUser } from "@/features/auth/hooks/useUser";
import { useLanguage } from "@/i18n/useLanguage";
import { hasAuthToken } from "@/i18n";

const SOCIALS = [
	{
		label: "Instagram",
		icon: "fa-instagram",
		href: "https://www.instagram.com/dekamas.kursevi/",
	},
	{
		label: "Facebook",
		icon: "fa-facebook-f",
		href: "https://www.facebook.com/dekamas.kursevi",
	},
	{
		label: "LinkedIn",
		icon: "fa-linkedin-in",
		href: "https://www.linkedin.com/company/dekamas/",
	},
];

function Footer() {
	const { t } = useTranslation();
	const { isLoggedIn, isLoading, user } = useUser();
	const { enabled: languageSwitchEnabled } = useLanguage();

	// Guests only: a signed-in user's language belongs to their account and
	// is changed in Settings. The token check settles before /auth/whoami/
	// does, so the links never flash for a signed-in user.
	const isGuest = !hasAuthToken() || (!isLoading && !isLoggedIn);

	return (
		<div className={style.footer}>
			<Container>
				<div className={style.top}>
					<div className={style.brand}>
						<Logo />
						<p className={style.tagline}>
							{t("common:footer.tagline")}
						</p>
						<EuFunding className={style.euFunding} />
					</div>

					<FooterColumn title={t("common:footer.platform")}>
						<Link to="/courses">{t("common:nav.courses")}</Link>
						<Link to="/paths">{t("common:nav.paths")}</Link>
						<Link to="/plans">{t("common:nav.plans")}</Link>
					</FooterColumn>

					<FooterColumn title={t("common:footer.account")}>
						{isLoggedIn ? (
							<>
								<Link to={`/u/${user?.username}`}>
									{t("common:nav.profile")}
								</Link>
								<Link to="/settings">
									{t("common:nav.settings")}
								</Link>
							</>
						) : (
							<>
								<Link to="/login">{t("common:nav.login")}</Link>
								<Link to="/register">
									{t("common:nav.register")}
								</Link>
							</>
						)}
					</FooterColumn>

					<FooterColumn title={t("common:footer.support")}>
						<Link to="/support">{t("common:nav.help")}</Link>
						<Link to="/privacy-policy">
							{t("common:nav.privacy")}
						</Link>
					</FooterColumn>
				</div>

				<div className={style.content}>
					<div className={style.legal}>
						<Link to="/privacy-policy" className={style.legalLink}>
							{t("common:nav.privacy")}
						</Link>
						<Icon icon="fa-pipe" className={style.dot} />
						<Link
							to="/terms-and-conditions"
							className={style.legalLink}
						>
							{t("common:nav.terms")}
						</Link>
						{languageSwitchEnabled && isGuest && (
							<>
								<Icon icon="fa-pipe" className={style.dot} />
								<LanguageSwitcher variant="links" />
							</>
						)}
					</div>

					<div className={style.socials}>
						{SOCIALS.map((social) => (
							<a
								key={social.label}
								href={social.href}
								target="_blank"
								rel="noreferrer noopener"
								aria-label={social.label}
								className={style.social}
							>
								<Icon icon={social.icon} iconType="fa-brands" />
							</a>
						))}
					</div>

					<div className={style.rights}>
						{t("common:footer.rights", {
							year: new Date().getFullYear(),
						})}
					</div>
				</div>
			</Container>
		</div>
	);
}

function FooterColumn({ title, children }) {
	return (
		<div className={style.column}>
			<div className={style.columnTitle}>{title}</div>
			<div className={style.columnLinks}>{children}</div>
		</div>
	);
}

export default Footer;
